import React, { useEffect, useRef, useState } from 'react';
import { List, ListItem, ListItemText, Divider, Chip, Typography, Box } from '@mui/material';
import axios from 'axios';

function titleCase(s = '') {
  if (!s) return '';
  return s
    .toString()
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ');
}

/**
 * BookedUsersDisplay
 * - Fetches appointment records and aggregates unique users
 * - Shows each user's ID, username, and count of appointments
 * - Sorts by numeric user ID when possible, otherwise by string
 */
const BookedUsersDisplay = ({ apiPath = `${process.env.REACT_APP_API_BASE_URL}/Appointment/pending-appointments`, pollInterval = 3000, date, showDate = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // raw appointments from last fetch (kept in a ref to diff quickly)
  const prevRawRef = useRef(null);
  // visible queue (max 5) shown on the welcome page
  const [displayedUsers, setDisplayedUsers] = useState([]);

  // Helper: aggregate raw appointments into user summaries and attach latest appointment date
  const aggregate = (raw = []) => {
    const map = new Map();
    for (let i = 0; i < raw.length; i++) {
      const a = raw[i] || {};
      const name = (a.MAD_FULL_NAME ?? a.username ?? a.name ?? 'Unknown').toString();
      const cnt = Number(a.MAD_PATIENT_NO ?? a.MAD_PATIENT_COUNT ?? 1) || 1;
      // capture appointment timestamp if present
  const whenRaw = a.appointmentDate ?? a.MAD_APPOINTMENT_DATE ?? a.MAD_APPOINMENT_DATE ?? a.date ?? a.MAD_DATE ?? null;
      const when = whenRaw ? new Date(whenRaw) : null;
      if (!map.has(name)) map.set(name, { name, count: 0, firstSeen: i });
      const rec = map.get(name);
      rec.count += cnt;
      if (when) {
        if (!rec.latestDate || when > rec.latestDate) rec.latestDate = when;
      }
    }
    const arr = Array.from(map.values());
    // preserve previous sorting behavior: by count ascending, then newer-firstSeen last
    arr.sort((x, y) => {
      if (x.count !== y.count) return x.count - y.count;
      return (y.firstSeen ?? 0) - (x.firstSeen ?? 0);
    });
    return arr.map((x) => ({ ...x, displayName: titleCase(x.name), latestDate: x.latestDate }));
  };

  // ref-based lifecycle controls so we can trigger the same fetch from an external event
  const mountedRef = useRef(true);
  const initialLoadRef = useRef(true);
  const timerRef = useRef(null);

  // make the fetch function available at component scope so an external event can call it
  const fetchAndDiff = React.useCallback(async () => {
    if (!mountedRef.current) return;
    if (initialLoadRef.current) setLoading(true);
    try {
      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);
      const target = date ? `${apiPath}/${date}` : `${apiPath}/${todayStr}`;
      const res = await axios.get(target);
      if (!mountedRef.current) return;
      const raw = Array.isArray(res.data) ? res.data : [];

      // first-time initialization
      if (!prevRawRef.current) {
        prevRawRef.current = raw.map((r) => JSON.stringify(r));
        const ag = aggregate(raw);
        setDisplayedUsers(ag.slice(0, 5));
        setError('');
        if (initialLoadRef.current) {
          setLoading(false);
          initialLoadRef.current = false;
        }
        return;
      }

      // detect newly added raw appointment records (by JSON string diff)
      const prevSet = new Set(prevRawRef.current || []);
      const newRecords = [];
      for (const item of raw) {
        const s = JSON.stringify(item);
        if (!prevSet.has(s)) newRecords.push(item);
      }

      // update prev snapshot for next diff
      prevRawRef.current = raw.map((r) => JSON.stringify(r));

      if (newRecords.length > 0) {
        const ag = aggregate(raw);
        setDisplayedUsers((prev) => {
          let next = [...prev];
          for (const nr of newRecords) {
            const name = (nr.MAD_FULL_NAME ?? nr.username ?? nr.name ?? 'Unknown').toString();
            const agUser = ag.find((u) => u.name === name) || { name, count: Number(nr.MAD_PATIENT_NO ?? 1) || 1, displayName: titleCase(name), latestDate: nr.appointmentDate ?? nr.MAD_APPOINTMENT_DATE ?? nr.MAD_APPOINMENT_DATE ?? nr.date ?? nr.MAD_DATE ?? null };
            if (next.length >= 5) next = next.slice(1);
            next = [...next, agUser];
          }
          const seen = new Set();
          const deduped = [];
          for (const it of next) {
            if (!seen.has(it.name)) {
              deduped.push(it);
              seen.add(it.name);
            }
          }
          if (deduped.length > 5) return deduped.slice(-5);
          return deduped;
        });
      } else {
        const ag = aggregate(raw);
        setDisplayedUsers((prev) => {
          if ((prev && prev.length > 0) && ag.length >= prev.length) return prev;
          return ag.slice(0, 5);
        });
      }
      setError('');
    } catch (e) {
      if (!mountedRef.current) return;
      const status = e?.response?.status;
      const msg = e?.message || 'Unknown error';
      setError(status ? `No appointments available ` : `Unable to load appointments: ${msg}`);
      // eslint-disable-next-line no-console
      console.warn('BookedUsersDisplay fetch error:', e);
    } finally {
      if (initialLoadRef.current) {
        setLoading(false);
        initialLoadRef.current = false;
      }
    }
  }, [apiPath, date]);

  // polling effect — runs fetchAndDiff initially and on interval
  useEffect(() => {
    mountedRef.current = true;
    fetchAndDiff();
    timerRef.current = setInterval(fetchAndDiff, pollInterval);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [apiPath, pollInterval, date, fetchAndDiff]);

  // listen for external refresh events (e.g., dispatched by other components)
  useEffect(() => {
    const handler = () => {
      try {
        fetchAndDiff();
      } catch (e) {
        // swallow — fetchAndDiff logs its own errors
      }
    };
    window.addEventListener('bookings:refresh', handler);
    return () => window.removeEventListener('bookings:refresh', handler);
  }, [apiPath, pollInterval, date, fetchAndDiff]);

  if (loading) return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Loading...</Typography>;
  if (error) return <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{error}</Typography>;
  if (!displayedUsers || displayedUsers.length === 0) return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>No recent bookings.</Typography>;

  // Show the first 5 users (or fewer if not enough)
  const visible = displayedUsers.slice(0, 5);

  return (
    <List sx={{ p: 0, maxHeight: 360, overflow: 'hidden' }}>
      {visible.map((u, relIdx) => {
        const isFirst = relIdx === 0;
        return (
          <React.Fragment key={u.name}>
            <ListItem
              sx={{
                py: 1,
                px: 1,
                borderRadius: 2,
                mb: isFirst ? 1 : 0,
                background: isFirst
                  ? 'linear-gradient(90deg, rgba(240, 225, 18, 0.62), rgba(204, 236, 18, 0.86))'
                  : 'transparent',
                backdropFilter: isFirst ? 'blur(4px)' : 'none',
                alignItems: 'center',
              }}
            >
              <ListItemText
                primary={<Typography sx={{ color: 'white', fontWeight: isFirst ? 700 : 600, fontSize: isFirst ? 18 : 'inherit' }}>{u.displayName}</Typography>}
                secondary={showDate && u.latestDate ? new Date(u.latestDate).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.75)', fontSize: isFirst ? 12 : 11 } }}
              />
              {isFirst ? (
                <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#1672ff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    boxShadow: '0 6px 18px rgba(22,114,255,0.25)'
                  }}>
                    {`#${u.count}`}
                  </Box>
                </Box>
              ) : (
                <Chip label={`#${u.count}`} size="small" color="primary" />
              )}
            </ListItem>
            {relIdx < visible.length - 1 && <Divider sx={{ opacity: 0.12 }} />}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default BookedUsersDisplay;
